import * as Dialog from '@radix-ui/react-dialog';
import { makeClassName } from '@siteless/util';
import CloseIcon from '../CloseIcon';
import { motion, AnimatePresence } from 'motion/react';
import Loader from '../Loader';
import { BaseContainerComponentPropsType } from '../../util/types';
import ArrowLeftIcon from '../ArrowLeftIcon';
import { PopWrapper } from '../PopWrapper';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { uniqueId } from 'lodash';

type ModalPropType = BaseContainerComponentPropsType & {
  isOpen: boolean;
  onClose: () => void;
  onCloseStart?: () => void;
  onBack?: () => void;
  onEntered?: () => void;
  disableBodyPadding?: boolean;
  zIndex?: number;
};

let modalIds: {
  current: string[];
} = {
  current: [],
};

// heads up that TS complains when this isn't a const
const Modal = ({
  isOpen,
  onClose,
  onCloseStart,
  className,
  children,
  disableBodyPadding,
  onBack,
  onEntered,
  zIndex: propsZIndex,
}: ModalPropType) => {
  let modalIdRef = useRef(uniqueId());
  let [modalStackPostion, setModalStackPosition] = useState(0);
  let [isClosingInternally, setIsClosingInternally] = useState(false);
  let onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    setIsClosingInternally(false);
  }, [isOpen]);

  let handleClose = () => {
    setIsClosingInternally(true);
    onCloseStart?.();
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    let modalId = modalIdRef.current;
    modalIds.current = modalIds.current.concat(modalId);
    setModalStackPosition(modalIds.current.length);
    return () => {
      modalIds.current = modalIds.current.filter((item) => item !== modalId);
    };
  }, [isOpen]);

  let zIndex =
    typeof propsZIndex === 'number' ? propsZIndex : 10 + modalStackPostion;

  return (
    <Dialog.Root
      open={isOpen || isClosingInternally}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <Dialog.Portal forceMount>
        <AnimatePresence onExitComplete={() => {
          setIsClosingInternally(false);
          onCloseRef.current();
        }}>
          {isOpen && !isClosingInternally && (
            <>
              <Dialog.Overlay forceMount className="fixed inset-0" style={{ zIndex }} />
              <Dialog.Content forceMount asChild onOpenAutoFocus={(e) => e.preventDefault()} aria-describedby={undefined}>
                <motion.div
                  variants={{
                    hidden: { x: '100%' },
                    visible: { x: '0%', transition: { type: 'tween', ease: [0.25, 0.1, 0.25, 1], duration: 0.3 } },
                    exit: { x: '100%', transition: { type: 'tween', ease: [0.32, 0.72, 0, 1], duration: 0.35 } },
                  }}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onAnimationComplete={(definition: string) => {
                    if (definition === 'visible') onEntered?.();
                  }}
                  style={{ zIndex }}
                  className="Modal__content bg-neutral-0 fixed inset-0 flex flex-col overflow-hidden border-4 border-neutral-100 text-purple-800 outline-none"
                >
                  {Boolean(onBack) && (
                    <ArrowLeftIcon
                      className="__back-icon absolute z-20 rounded-full"
                      onClick={onBack}
                    />
                  )}
                  <PopWrapper
                    className="__close-icon absolute top-2 right-2 z-20 h-10 w-10 cursor-pointer rounded-full bg-neutral-100 p-2"
                    scaleTo={1.5}
                    onClick={handleClose}
                  >
                    <CloseIcon />
                  </PopWrapper>
                  <Dialog.Title className="sr-only">Modal</Dialog.Title>
                  <div
                    className={makeClassName(
                      '__body sm:mx-auto sm:w-[500px]',
                      disableBodyPadding && '--no-padding',
                      className,
                    )}
                  >
                    {children}
                  </div>
                </motion.div>
              </Dialog.Content>
            </>
          )}
        </AnimatePresence>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

Modal.MainText = ({ className, children }: BaseContainerComponentPropsType) => (
  <div className={makeClassName('_main-text', className)}>{children}</div>
);

Modal.BottomActions = ({
  className,
  children,
}: BaseContainerComponentPropsType) => (
  <div
    className={makeClassName('flex flex-none flex-col py-2 px-5', className)}
  >
    {children}
  </div>
);

Modal.Loader = ({ className }: BaseContainerComponentPropsType) => (
  <div
    className={makeClassName(
      'flex flex-1 items-center justify-center',
      className,
    )}
  >
    <Loader />
  </div>
);

export default Modal;
